import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useParkingWizardStore } from 'Stores/ParkingWizardStore';
import { createParkingReservation, CreateParkingReservationRequest } from 'Services/Api/_HumanWritten/ParkingService';

export function ParkingPaymentStep() {
	const navigate = useNavigate();
	const {
		locationId,
		vehicles,
		note,
		totalPrice,
		transactionId,
		reservationId,
		humanReadableId,
		setReservationResult,
		setStep,
		setError,
		setLoading,
		isLoading,
		reset,
	} = useParkingWizardStore();

	const [reservationCreated, setReservationCreated] = useState(!!reservationId);

	const handleCreateReservation = async () => {
		if (!locationId) {
			setError('No location selected.');
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const request: CreateParkingReservationRequest = {
				locationId,
				note: note || undefined,
				vehicles: vehicles.map(v => ({
					parkingVehicleTypeId: v.parkingVehicleTypeId,
					startDate: v.startDate,
					endDate: v.endDate,
				})),
			};

			const result = await createParkingReservation(request);
			setReservationResult(
				result.reservationId,
				result.reservationIds,
				result.transactionId,
				result.humanReadableId,
				result.humanReadableIds,
			);
			setReservationCreated(true);
		} catch {
			setError('Failed to create reservation. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	const handleBack = () => {
		if (!reservationCreated) {
			setError(null);
			setStep('review');
		}
	};

	const handleComplete = () => {
		reset();
		navigate('/parking/success');
	};

	if (isLoading) {
		return <div className="parking-wizard__loading">Creating your reservation...</div>;
	}

	if (reservationCreated && transactionId) {
		return (
			<div className="parking-wizard__step parking-payment-step">
				<h2>Reservation Created</h2>
				<div className="parking-payment-step__success">
					<p>Your parking reservation has been created.</p>
					<p>
						<strong>Booking Reference:</strong> {humanReadableId}
					</p>
					<p>
						<strong>Total:</strong> ${totalPrice?.toFixed(2)}
					</p>
					<p>
						Transaction ID: <code>{transactionId}</code>
					</p>
					<p className="text-muted">
						Please proceed to complete payment through the standard payment flow using the transaction ID
						above.
					</p>
				</div>
				<div className="parking-wizard__actions">
					<button type="button" className="btn btn-primary" onClick={handleComplete}>
						Done
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="parking-wizard__step parking-payment-step">
			<h2>Confirm & Pay</h2>
			<div className="parking-payment-step__summary">
				<p>
					<strong>Total:</strong> ${totalPrice?.toFixed(2)}
				</p>
				<p>Click below to create your reservation and proceed to payment.</p>
			</div>
			<div className="parking-wizard__actions">
				<button type="button" className="btn btn-outline-secondary" onClick={handleBack}>
					Back
				</button>
				<button type="button" className="btn btn-primary" onClick={handleCreateReservation}>
					Create Reservation
				</button>
			</div>
		</div>
	);
}
